/**
 * GST Calculation Service
 * Handles all GST-related calculations including ITC, output GST, and quarterly summaries
 */

// GST rates configuration
export const GST_RATES = {
  CGST: 9,    // Central GST
  SGST: 9,    // State GST
  IGST: 18,   // Integrated GST
  TOTAL: 18   // Total GST rate
};

/**
 * Calculate quarter and year from date
 */
export const getQuarterAndYear = (date) => {
  const d = new Date(date);
  const month = d.getMonth() + 1; // JavaScript months are 0-indexed
  const year = d.getFullYear();
  
  let quarter;
  if (month >= 4 && month <= 6) quarter = 'Q1';
  else if (month >= 7 && month <= 9) quarter = 'Q2';
  else if (month >= 10 && month <= 12) quarter = 'Q3';
  else quarter = 'Q4';
  
  // Financial year calculation
  const financialYear = month >= 4 ? year : year - 1;
  
  return { quarter, year: financialYear };
};

/**
 * Calculate ITC (Input Tax Credit) from purchase amount and GST
 */
export const calculateITC = (purchaseAmount, gstAmount) => {
  const totalAmount = parseFloat(purchaseAmount) || 0;
  const gst = parseFloat(gstAmount) || 0;
  
  // ITC is the GST paid on purchases that can be claimed as credit
  return {
    itcAmount: gst,
    baseAmount: totalAmount - gst,
    gstAmount: gst,
    totalAmount: totalAmount
  };
};

/**
 * Calculate output GST from sale amount
 */
export const calculateOutputGST = (saleAmount, isGstInclusive = true, discount = 0) => {
  const amount = parseFloat(saleAmount) || 0;
  const numDiscount = parseFloat(discount) || 0;
  const amountAfterDiscount = amount - numDiscount;
  
  if (isGstInclusive) {
    // GST is included in the sale amount
    const baseAmount = amountAfterDiscount / (1 + GST_RATES.IGST / 100);
    const gstAmount = amountAfterDiscount - baseAmount;
    
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      igst: Math.round(gstAmount * 100) / 100,
      totalAmount: amountAfterDiscount,
      discount: numDiscount
    };
  } else {
    // GST is exclusive - need to add GST
    const baseAmount = amountAfterDiscount;
    const gstAmount = (amountAfterDiscount * GST_RATES.IGST) / 100;
    const totalAmount = baseAmount + gstAmount;
    
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      igst: Math.round(gstAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      discount: numDiscount
    };
  }
};

/**
 * Calculate quarterly GST summary
 */
export const calculateQuarterlySummary = (purchases = [], sales = []) => {
  // Calculate total purchases and ITC
  const purchasesSummary = purchases.reduce((acc, purchase) => {
    const amount = parseFloat(purchase.purchaseAmount) || 0;
    const gst = parseFloat(purchase.gstAmount) || 0;
    
    acc.totalPurchases += amount;
    acc.totalITC += gst;
    acc.baseAmount += (amount - gst);
    
    return acc;
  }, {
    totalPurchases: 0,
    totalITC: 0,
    baseAmount: 0
  });

  // Calculate total sales and output GST
  const salesSummary = sales.reduce((acc, sale) => {
    const amount = parseFloat(sale.saleAmount) || 0;
    const gst = parseFloat(sale.gstAmount) || 0;
    
    acc.totalSales += amount;
    acc.totalOutputGST += gst;
    acc.baseAmount += (amount - gst);
    
    return acc;
  }, {
    totalSales: 0,
    totalOutputGST: 0,
    baseAmount: 0
  });

  // Calculate net GST payable
  const netGSTPayable = salesSummary.totalOutputGST - purchasesSummary.totalITC;
  
  return {
    purchases: {
      count: purchases.length,
      totalAmount: Math.round(purchasesSummary.totalPurchases * 100) / 100,
      baseAmount: Math.round(purchasesSummary.baseAmount * 100) / 100,
      totalITC: Math.round(purchasesSummary.totalITC * 100) / 100
    },
    sales: {
      count: sales.length,
      totalAmount: Math.round(salesSummary.totalSales * 100) / 100,
      baseAmount: Math.round(salesSummary.baseAmount * 100) / 100,
      totalOutputGST: Math.round(salesSummary.totalOutputGST * 100) / 100
    },
    netGSTPayable: Math.round(netGSTPayable * 100) / 100,
    netGSTRefundable: netGSTPayable < 0 ? Math.abs(netGSTPayable) : 0
  };
};

/**
 * Validate GST number format
 */
export const validateGSTNumber = (gstNumber) => {
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstRegex.test(gstNumber);
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

/**
 * Get financial year string from date
 */
export const getFinancialYearString = (date) => {
  const { year } = getQuarterAndYear(date);
  return `${year}-${(year + 1).toString().substr(-2)}`;
};
